import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  useGetProjectQuery,
  usePatchProjectMutation,
  useGetBoardCompaniesQuery,
  useGetBoardContactsQuery,
  useGetBoardMarketTypesQuery,
  useGetBoardUsersQuery,
} from 'app/services/projectBoardApi';
import {
  setGeneralInfoSubTab,
  openReassign,
  openTextEdit,
} from 'app/slices/projectBoardSlice';
import type { IContact, IProjectMain } from 'models';
import { useBoardCurrentUser, useTabAccess } from '../hooks/useProjectBoardData';
import NetworkEditingPopup from './NetworkEditingPopup';
import {
  PARTNER_ROLES,
  isProjectNameValid,
  syncPendingReportsProjectName,
  formatBoardNumber,
} from '../projectBoardUtils';

// ---------------------------------------------------------------------------
// TextField — draft-holding text input with Backbone `change` semantics:
// commits on blur ONLY when the value actually changed; re-syncs from the
// server value when not focused (model-change re-render parity). Optional
// `.text-clear` icon clears + commits '' (clearTexField: val('') + change+blur).
// ---------------------------------------------------------------------------
interface TextFieldProps {
  name: string;
  value: string;
  commit: (name: string, raw: string) => void;
  className?: string;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  disabled?: boolean;
  clear?: boolean;
  clearClassName?: string;
  onKeyUpValue?: (value: string) => void;
  formatterType?: 'integer' | 'decimal' | 'float';
  dataMin?: string;
  dataStep?: string;
  dataMax?: string;
}

const TextField = (props: TextFieldProps) => {
  const [draft, setDraft] = useState(props.value ?? '');
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(props.value ?? '');
  }, [props.value]);

  const commitIfChanged = (v: string) => {
    if (v !== (props.value ?? '')) props.commit(props.name, v);
  };

  return (
    <>
      <input
        ref={ref}
        type="text"
        name={props.name}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commitIfChanged(draft)}
        onKeyUp={
          props.onKeyUpValue
            ? (e) => props.onKeyUpValue?.((e.target as HTMLInputElement).value)
            : undefined
        }
        className={props.className}
        placeholder={props.placeholder}
        minLength={props.minLength}
        maxLength={props.maxLength}
        disabled={props.disabled}
        data-formatter_type={props.formatterType}
        data-min={props.dataMin}
        data-step={props.dataStep}
        data-max={props.dataMax}
      />
      {props.clear && (
        <i
          className={props.clearClassName ?? 'text-clear icon-close-1'}
          onClick={() => {
            // Deviation (data-safety): Backbone's delegated clear handler still
            // fires on locked projects via synthetic events; we no-op instead.
            if (props.disabled) return;
            setDraft('');
            commitIfChanged('');
          }}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// ProjectMainForm — ProjectMainView (project/view/project_main.js →
// project_main.tmpl, 803 lines), inventory §3.8 / parity 40–64. Renders into
// the `.main-inputs` mount of the General Info composite. Every field change
// persists via PATCH /api/v1.0/projects/{id} with only the changed field(s).
// ---------------------------------------------------------------------------
const ProjectMainForm = ({ projectId }: { projectId: number }) => {
  const dispatch = useAppDispatch();
  const subTab = useAppSelector((s) => s.projectBoard.generalInfoSubTab);

  const { data: project } = useGetProjectQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const companiesQ = useGetBoardCompaniesQuery();
  const contactsQ = useGetBoardContactsQuery();
  const { data: markets } = useGetBoardMarketTypesQuery();
  const { data: users } = useGetBoardUsersQuery();
  const { data: currentUser } = useBoardCurrentUser();
  const permission = useTabAccess(projectId, 'project');
  const [patchProject] = usePatchProjectMutation();

  const [nameInvalid, setNameInvalid] = useState(false);
  // "Network Users Editing" pending confirmation (grant/revoke popup open).
  const [networkEditingPopup, setNetworkEditingPopup] = useState<
    'grant' | 'revoke' | null
  >(null);

  const companies = companiesQ.data;
  const contacts = contactsQ.data;

  // Cross-window Partners-app sync (parity 56): on window focus, localStorage
  // flags trigger companies/contacts refetch, then the flags are cleared.
  const refetchCompanies = companiesQ.refetch;
  const refetchContacts = contactsQ.refetch;
  useEffect(() => {
    const onFocus = () => {
      const ls = window.localStorage;
      if (
        ls.getItem('edit:company:name') ||
        ls.getItem('delete:company:name') ||
        ls.getItem('add:company:name')
      ) {
        Promise.all([refetchCompanies(), refetchContacts()]).then(() => {
          ls.removeItem('add:company:name');
          ls.removeItem('edit:company:name');
          ls.removeItem('delete:company:name');
        });
      }
      if (ls.getItem('add:company:contact')) {
        refetchContacts().then(() => {
          ls.removeItem('add:company:contact');
        });
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetchCompanies, refetchContacts]);

  // Contacts grouped by company (render: _.groupBy(floor(company_id))).
  const companyContacts = useMemo(() => {
    const map = new Map<number, IContact[]>();
    (contacts ?? []).forEach((c) => {
      const key = Math.floor(Number(c.company_id));
      const list = map.get(key);
      if (list) list.push(c);
      else map.set(key, [c]);
    });
    return map;
  }, [contacts]);

  if (!project || !companies || !contacts || !markets || !users || !currentUser) {
    return null; // Backbone renders after current.user + markets + users resolve
  }

  const main = project;
  const locked = !permission || main.editable === false;

  // ---- Network Users Editing gating (project_main.js render 126-131 +
  // project_main.tmpl 157-161, mirroring the serializer's validate()):
  // disabled for network users, for non-owners who are not CMS admins, and —
  // with the Backbone tooltip — once the critical task has started.
  const networkEditingIsOwner = currentUser.id === main.user_id;
  const networkEditingIsCmsAdmin = !!currentUser.is_cms_admin;
  const networkEditingIsNetworkUser = currentUser.access_type === 'network';
  const networkEditingDisabled =
    networkEditingIsNetworkUser ||
    (!networkEditingIsOwner && !networkEditingIsCmsAdmin) ||
    !!main.is_critical_task_started;

  // changeNetworkEditingEnabled: check → disclaimer popup, uncheck → revoke
  // popup; nothing persists until the popup confirms. Cancel closes with no
  // HTTP — the checkbox stays controlled by main.network_editing_enabled.
  const confirmNetworkEditing = () => {
    patchProject({
      id: projectId,
      body: { network_editing_enabled: networkEditingPopup === 'grant' },
    });
    setNetworkEditingPopup(null);
  };

  // ---- changeInput (autosave via model PATCH) ----
  const commit = (name: string, raw: string) => {
    if (name === 'elevation' || name === 'sccr') {
      const min = 0;
      const max = name === 'sccr' ? 1000 : 25000;
      const stripped = raw.replace(/,/g, '');
      let v: number | null;
      if (stripped === '') v = null;
      else {
        const n = parseInt(stripped, 10);
        v = Number.isNaN(n) ? null : Math.min(Math.max(n, min), max);
      }
      patchProject({ id: projectId, body: { [name]: v } });
      return;
    }
    if (name === 'name') {
      const valid = isProjectNameValid(raw);
      setNameInvalid(!valid);
      if (!valid) return; // checkField fails → no save (parity 41)
      patchProject({ id: projectId, body: { name: raw } });
      // header generated-reports list rename sync (parity 42)
      syncPendingReportsProjectName(projectId, raw);
      return;
    }
    // '' → null mirrors legacy changeInput (project_main.js:212) — this is
    // also the custom_quote_number / custom_job_number clear path, so an
    // emptied text input PATCHes null, never "" (QA 2026-07: the "" 400s).
    patchProject({ id: projectId, body: { [name]: raw === '' ? null : raw } });
  };

  // ---- changeSelect: parseInt; '--'/none → null (NaN serializes to null) ----
  const commitSelect = (name: string, raw: string) => {
    const v = parseInt(raw, 10);
    patchProject({ id: projectId, body: { [name]: Number.isNaN(v) ? null : v } });
  };

  // ---- makeChangable (.custom_value_select): PATCH the pair via must_be_changed ----
  // dev b9d361723c (project_main.js:374-386): uncheck now sends the paired
  // custom_* field as null (was ''). On check, dev's `.replace('is_custom_',
  // null)` coerces null to the string "null", so `get('nullquote_number')` is
  // undefined and JSON.stringify DROPS the paired field from the PATCH — the
  // old seed-from-auto-value no longer reaches the server. Mirror that net
  // contract: check → {is_custom_*: true}; uncheck → {custom_*: null,
  // is_custom_*: false}.
  const onCustomValueSelect = (
    selName: 'is_custom_quote_number' | 'is_custom_job_number',
    raw: string
  ) => {
    const val = !!raw; // '' → false, 'true' → true
    const inputName = selName.replace('is_', ''); // custom_quote_number
    if (val) {
      document.querySelector<HTMLInputElement>(`input[name=${inputName}]`)?.focus();
    }
    patchProject({
      id: projectId,
      body: val ? { [selName]: true } : { [inputName]: null, [selName]: false },
    });
  };

  const openEdit = (name: 'warranty' | 'submittal_cover_note') =>
    dispatch(openTextEdit(name));

  const assignedUser = users.find((u) => u.id === main.user_id);
  const coId = (field: string): number | null => {
    const v = main[`${field}_co_id`];
    return typeof v === 'number' ? v : null;
  };
  const contactId = (field: string): number | null => {
    const v = main[`${field}_contact_id`];
    return typeof v === 'number' ? v : null;
  };
  const fmtInt = (v: IProjectMain['sccr']): string =>
    v === null || v === undefined || v === '' ? '' : formatBoardNumber(v, 'integer');

  const selectSubTab = (tab: 'project' | 'partners') => (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setGeneralInfoSubTab(tab));
  };

  return (
    <div className="w-100 h-100">
      {/* Sub-tab bar (Project / Partners) — parity 40 */}
      <div className="col-xs-12 sub-tab-menu nav nav-tabs">
        <ul className="project_partner-nav-tabing project-menu__nav row">
          <li
            className={`project_partner_tab${subTab === 'project' ? ' active' : ''}`}
            id="project_list"
          >
            <a
              className={subTab === 'project' ? 'selected' : ''}
              data-value="project"
              data-toggle="tab"
              onClick={selectSubTab('project')}
            >
              Project
            </a>
          </li>
          {/* Whitespace text node between the two <li>s — legacy
              project_main.tmpl:5-11 has a Handlebars newline here (~4px gap);
              JSX strips it, so restore it explicitly. */}
          {' '}
          <li
            className={`project_partner_tab${subTab === 'partners' ? ' active' : ''}`}
            id="partners_list"
          >
            <a
              className={subTab === 'partners' ? 'selected' : ''}
              data-value="partners"
              data-toggle="tab"
              onClick={selectSubTab('partners')}
            >
              Partners
            </a>
          </li>
        </ul>
      </div>

      {/* ---- Project sub-tab ---- */}
      <div
        className={`col-xs-12 tab-pane fade${subTab === 'project' ? ' in active' : ''}`}
        id="tab_project"
      >
        {/* Name */}
        <div className="row  project-item">
          <div className="col-xs-4">
            <div className="row">
              <span className="name required">Name</span>
            </div>
          </div>
          <div className="col-xs-8">
            <div className="row">
              <TextField
                name="name"
                value={main.name ?? ''}
                commit={commit}
                minLength={3}
                maxLength={50}
                className={`depend_on_role${nameInvalid ? ' invalid-field' : ''}${
                  locked ? ' non-clickable' : ''
                }`}
                disabled={locked}
                clear
                onKeyUpValue={(v) => setNameInvalid(!isProjectNameValid(v))}
              />
              <span className="required-field-note">must be 3 - 50 characters</span>
            </div>
          </div>
        </div>

        {/* Project Number */}
        <div className="row">
          <div className="col-xs-12 project-item">
            <div className="row">
              <div className="col-xs-4">
                <div className="row">
                  <span className="name"> Project Number </span>
                </div>
              </div>
              <div className="col-xs-5 pr-0 project-job-number">
                <div className="row">
                  <TextField
                    name="custom_quote_number"
                    value={main.quote_number ?? ''}
                    commit={commit}
                    className={`depend_on_role long-input adress-one too_long_text${
                      main.is_custom_quote_number ? '' : ' non-clickable'
                    }${locked ? ' non-clickable' : ''}`}
                    placeholder="Job Number"
                    disabled={locked}
                  />
                </div>
              </div>
              <div className="col-xs-3">
                <div className="row">
                  <select
                    className={`number_select custom_value_select${
                      locked ? ' non-clickable' : ''
                    }`}
                    name="is_custom_quote_number"
                    required
                    disabled={locked}
                    value={main.is_custom_quote_number ? 'true' : ''}
                    onChange={(e) =>
                      onCustomValueSelect('is_custom_quote_number', e.target.value)
                    }
                  >
                    <option value="">Sequential</option>
                    <option value="true">User defined</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Job Number */}
        <div className="row">
          <div className="col-xs-12 project-item">
            <div className="row ">
              <div className="col-xs-4">
                <div className="row">
                  <span className="name"> Job Number </span>
                </div>
              </div>
              <div className="col-xs-5 pr-0 project-job-number">
                <div className="row">
                  <TextField
                    name="custom_job_number"
                    value={main.job_number ?? ''}
                    commit={commit}
                    className={`depend_on_role long-input adress-one too_long_text${
                      main.is_custom_job_number ? '' : ' non-clickable'
                    }${locked ? ' non-clickable' : ''}`}
                    placeholder="Job Number"
                    disabled={locked}
                  />
                </div>
              </div>
              <div className="col-xs-3">
                <div className="row">
                  {/* Select disabled & non-clickable until is_critical_task_started (parity 44) */}
                  <select
                    className={`number_select custom_value_select${
                      main.is_critical_task_started ? '' : ' non-clickable'
                    }${locked ? ' non-clickable' : ''}`}
                    name="is_custom_job_number"
                    required
                    disabled={locked || !main.is_critical_task_started}
                    value={main.is_custom_job_number ? 'true' : ''}
                    onChange={(e) =>
                      onCustomValueSelect('is_custom_job_number', e.target.value)
                    }
                  >
                    <option value="">Sequential</option>
                    <option value="true">User defined</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Created On */}
        <div className="row">
          <div className="col-xs-12 project-item">
            <div className="row">
              <div className="col-xs-4">
                <div className="row">
                  <span className="name">Created On</span>
                </div>
              </div>
              <div className="col-xs-8">
                <div className="row">
                  <input
                    type="text"
                    value={main.create_date ?? ''}
                    readOnly
                    className="non-clickable "
                    disabled={locked}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Created By */}
        <div className="row">
          <div className="col-xs-12 project-item">
            <div className="row">
              <div className="col-xs-4">
                <div className="row">
                  <span className="name">Created By</span>
                </div>
              </div>
              <div className="col-xs-8">
                <div className="row">
                  <input
                    type="text"
                    value={main.created_by ?? ''}
                    readOnly
                    className="non-clickable "
                    disabled={locked}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Assigned to + Reassign */}
        <div className="row">
          <div className="col-xs-12  project-item">
            <div className="row project-output">
              <div className="col-xs-4 ">
                <div className="row">
                  <span className="name">Assigned to</span>
                </div>
              </div>
              <div className="col-xs-5 ">
                <div className="row">
                  <span className="">
                    {assignedUser && (
                      <input
                        value={`${assignedUser.first_name ?? ''} ${
                          assignedUser.middle_name ?? ''
                        } ${assignedUser.last_name ?? ''}`}
                        readOnly
                        className="non-clickable"
                        disabled={locked}
                      />
                    )}
                  </span>
                </div>
              </div>
              <div className="col-xs-3 ">
                <div className="row">
                  <button
                    id="reassign_btn"
                    className="add right"
                    disabled={locked}
                    onClick={() => dispatch(openReassign())}
                  >
                    Reassign
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Network Users Editing (project_main.tmpl 148-166) */}
        <div className="row network-editing-access-row">
          <div className="col-xs-12  project-item">
            <div className="row project-output">
              <div className="col-xs-4 ">
                <div className="row">
                  <span className="name">Network Users Editing</span>
                </div>
              </div>
              <div className="col-xs-8 ">
                <div className="row">
                  <input
                    type="checkbox"
                    name="network_editing_enabled"
                    id="network_editing_enabled"
                    checked={!!main.network_editing_enabled}
                    disabled={networkEditingDisabled}
                    title={
                      main.is_critical_task_started
                        ? 'No longer applicable — Critical Task started'
                        : undefined
                    }
                    onChange={(e) =>
                      setNetworkEditingPopup(e.target.checked ? 'grant' : 'revoke')
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address line 1 */}
        <div className="row">
          <div className="address col-xs-12  project-item">
            <div className="row">
              <div className="col-xs-4 ">
                <div className="row">
                  <span className="name">Address</span>
                </div>
              </div>
              <div className="total-inputs col-xs-8 ">
                <div className="row">
                  <TextField
                    name="address_line_one"
                    value={main.address_line_one ?? ''}
                    commit={commit}
                    className="long-input adress-one"
                    placeholder="Address Line 1"
                    minLength={0}
                    maxLength={30}
                    disabled={locked}
                    clear
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address line 2 */}
        <div className="row">
          <div className="address col-xs-12  project-item">
            <div className="row">
              <div className="total-inputs col-xs-8 col-xs-offset-4">
                <div className="row">
                  <TextField
                    name="address_line_two"
                    value={main.address_line_two ?? ''}
                    commit={commit}
                    className="long-input adress-two"
                    placeholder="Address Line 2"
                    minLength={0}
                    maxLength={30}
                    disabled={locked}
                    clear
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* City / State / Postal code */}
        <div className="row">
          <div className="address col-xs-12  project-item">
            <div className="row">
              <div className="total-inputs col-xs-8 col-xs-offset-4">
                <div className="row">
                  <div className="col-xs-5">
                    <div className="row">
                      <TextField
                        name="city"
                        value={main.city ?? ''}
                        commit={commit}
                        className="too_long_text"
                        placeholder="City"
                        minLength={0}
                        maxLength={30}
                        disabled={locked}
                        clear
                        clearClassName="text-clear icon-close-1 text-clear-city"
                      />
                    </div>
                  </div>
                  <div className="col-xs-3">
                    <div className="">
                      <TextField
                        name="state"
                        value={main.state ?? ''}
                        commit={commit}
                        className="too_long_text"
                        placeholder="State"
                        minLength={0}
                        maxLength={3}
                        disabled={locked}
                      />
                    </div>
                  </div>
                  <div className="col-xs-4">
                    <div className="margin-left row">
                      <TextField
                        name="postal_code"
                        value={main.postal_code ?? ''}
                        commit={commit}
                        className="too_long_text"
                        placeholder="Postal code"
                        minLength={0}
                        maxLength={12}
                        disabled={locked}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Country */}
        <div className="row">
          <div className="address col-xs-12  project-item">
            <div className="row">
              <div className="total-inputs col-xs-8 col-xs-offset-4">
                <div className="row">
                  <div className="col-xs-12">
                    <div className="row">
                      <TextField
                        name="country"
                        value={main.country ?? ''}
                        commit={commit}
                        className="too_long_text"
                        placeholder="Country"
                        minLength={0}
                        maxLength={15}
                        disabled={locked}
                        clear
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="on_bottom">
          {/* SCCR */}
          <div className="row">
            <div className="address col-xs-12  project-item">
              <div className="row project-output">
                <div className="col-xs-4">
                  <div className="row">
                    <span className="name">SCCR</span>
                    <span className="unit unit-right right">KA</span>
                  </div>
                </div>
                <div className="col-xs-8">
                  <div className="row">
                    <TextField
                      name="sccr"
                      value={fmtInt(main.sccr)}
                      commit={commit}
                      className="xs-input has-unit-after"
                      placeholder=""
                      disabled={locked}
                      formatterType="integer"
                      dataMin="0"
                      dataStep="1"
                      dataMax="1000"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Altitude */}
          <div className="row">
            <div className="address col-xs-12  project-item">
              <div className="row project-output">
                <div className="col-xs-4">
                  <div className="row">
                    <span className="name">Altitude</span>
                    <span className="unit unit-right right">ft</span>
                  </div>
                </div>
                <div className="col-xs-8">
                  <div className="row">
                    <TextField
                      name="elevation"
                      value={fmtInt(main.elevation)}
                      commit={commit}
                      className="xs-input has-unit-after"
                      placeholder=""
                      disabled={locked}
                      formatterType="integer"
                      dataMin="0"
                      dataStep="1"
                      dataMax="25000"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Warranty */}
          <div className="row">
            <div className="address col-xs-12  project-item">
              <div className="row project-output">
                <div className="col-xs-4">
                  <div className="row">
                    <span className="name">Warranty</span>
                  </div>
                </div>
                <div className="col-xs-8">
                  <div className="row submittal_cover_note_wrap contenteditable_input_wrap">
                    <input
                      data-input-name="warranty"
                      value={main.warranty ?? ''}
                      readOnly
                      className="contenteditable-input"
                      placeholder="800 characters maximum"
                      maxLength={800}
                      disabled={locked}
                      onClick={() => openEdit('warranty')}
                    />
                    {/* Pencil stays clickable even when locked (Backbone quirk — only
                        :input elements are disabled by the permission lockdown). */}
                    <i
                      data-input-name="warranty"
                      className="icon-edit icon-edit-contenteditable permission_check"
                      id="warranty_edit"
                      onClick={() => openEdit('warranty')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Market */}
          <div className="row">
            <div className="address col-xs-12  project-item">
              <div className="row project-output">
                <div className="col-xs-4">
                  <div className="row">
                    <span className="name name-left-padding name-market">Market</span>
                  </div>
                </div>
                <div className="col-xs-8">
                  <div className="row">
                    <select
                      className="xs-input market_id"
                      name="market_id"
                      required
                      disabled={locked}
                      value={main.market_id != null ? String(main.market_id) : 'none'}
                      onChange={(e) => commitSelect('market_id', e.target.value)}
                    >
                      <option value="none">--</option>
                      {markets.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submittal Cover Note */}
          <div className="row">
            <div className="address col-xs-12  project-item">
              <div className="row project-output">
                <div className="col-xs-4">
                  <div className="row">
                    <span className="name">Submittal Cover Note</span>
                  </div>
                </div>
                <div className="col-xs-8">
                  <div className="row submittal_cover_note_wrap contenteditable_input_wrap">
                    <input
                      className=""
                      type="checkbox"
                      name="submittal_cover_note_checkbox"
                      checked={!!main.submittal_cover_note_checkbox}
                      disabled={locked}
                      onChange={(e) =>
                        patchProject({
                          id: projectId,
                          body: { submittal_cover_note_checkbox: e.target.checked },
                        })
                      }
                    />
                    <input
                      data-input-name="submittal_cover_note"
                      value={main.submittal_cover_note ?? ''}
                      readOnly
                      className={`contenteditable-input${
                        main.submittal_cover_note_checkbox ? '' : ' non-clickable'
                      }`}
                      placeholder="800 characters maximum"
                      maxLength={800}
                      disabled={locked}
                      onClick={
                        main.submittal_cover_note_checkbox
                          ? () => openEdit('submittal_cover_note')
                          : undefined
                      }
                    />
                    <i
                      data-input-name="submittal_cover_note"
                      className={`icon-edit icon-edit-contenteditable permission_check${
                        main.submittal_cover_note_checkbox ? '' : ' non-clickable'
                      }`}
                      id="submittal_cover_note_edit"
                      onClick={
                        main.submittal_cover_note_checkbox
                          ? () => openEdit('submittal_cover_note')
                          : undefined
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* [NEG] hidden "UOM Pack" row (class `hide`, empty select) — not built (parity 57) */}
        </div>
      </div>

      {/* ---- Partners sub-tab (parity 61–64) ---- */}
      <div
        className={`col-xs-12 tab-pane fade ${subTab === 'partners' ? ' in active' : ''}`}
        id="tab_partners"
      >
        <div className="partner-title-block">
          <div className="row border-bottom">
            <div className="col-xs-4">
              <div className="row">
                <span className="name table-heading">PARTNER</span>
              </div>
            </div>
            <div className="col-xs-5 padding-left-0 padding-right-0">
              <div className="row">
                <span className="name table-heading col-xs-12">COMPANY</span>
              </div>
            </div>
            <div className="col-xs-3">
              <div className="contact-input-col col-xs-12">
                <div className="row">
                  <span className="name table-heading">CONTACT</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="company-contact-content">
          {PARTNER_ROLES.map(({ field, label }) => {
            const roleCoId = coId(field);
            const roleContacts =
              roleCoId != null ? companyContacts.get(roleCoId) ?? [] : [];
            return (
              <div className="company-contact-name row" key={field}>
                <div className="partner-info-item">
                  <div className="col-xs-4">
                    <div className="row">
                      <span className="name">{label}</span>
                    </div>
                  </div>
                  <div className="col-xs-5 padding-left-0 padding-right-0">
                    <select
                      className="medium-input first"
                      name={`${field}_co_id`}
                      required
                      disabled={locked}
                      value={roleCoId != null ? String(roleCoId) : 'none'}
                      onChange={(e) => commitSelect(`${field}_co_id`, e.target.value)}
                    >
                      <option value="none">--</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-xs-3">
                    <div className="contact-input-col">
                      <select
                        className="xs-input second"
                        name={`${field}_contact_id`}
                        required
                        disabled={locked}
                        value={
                          contactId(field) != null ? String(contactId(field)) : 'none'
                        }
                        onChange={(e) =>
                          commitSelect(`${field}_contact_id`, e.target.value)
                        }
                      >
                        <option value="none">--</option>
                        {roleContacts.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Network editing grant/revoke confirmation (appended to body in
          Backbone; an overlay wrapper here — same pattern as ReassignPopup) */}
      {networkEditingPopup && (
        <NetworkEditingPopup
          mode={networkEditingPopup}
          mfgName={main.mfg_name || ''}
          onConfirm={confirmNetworkEditing}
          onCancel={() => setNetworkEditingPopup(null)}
        />
      )}
    </div>
  );
};

export default ProjectMainForm;
